"use client";

import { useRef, useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { uniqBy } from "es-toolkit";
import { observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage, userSearch } from "@/api";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { Spinner } from "@/components/spinner/Spinner";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { useDirectChats } from "@/store/useDirectChats";
import { twMerge } from "@/utils/twMerge";
import { useDebouncedSearch } from "@/utils/useDebouncedSearch";
import { useLoadMore } from "@/utils/useLoadMore";

import {
  SECTION_HEADER_ACTION_CLASS_NAME,
  SECTION_HEADER_ACTION_ICON_CLASS_NAME,
} from "../SidebarSectionHeader";
import { useTreeItemControlTabIndex } from "../useSidebarNav";

import { DirectMessageUserpic, useDirectMessageLabel } from "./DirectMessagePerson";
import { useOpenDirectChat } from "./useOpenDirectChat";

import type { CurrentUser, UserShort } from "@/api";
import type { TeamModel } from "@/models/TeamModel";

const SEARCH_DEBOUNCE_MS = 200;

const PAGE_SIZE = 50;

// The search matches substrings of names and usernames, so a shorter query
// matches most of the user base.
const MIN_QUERY_LENGTH = 3;

type SearchResults = { query: string; users: UserShort[]; error: string | null };

/** The query to search for, or "" while it is too short to search. */
function searchableQuery(value: string) {
  const trimmed = value.trim();
  return trimmed.length >= MIN_QUERY_LENGTH ? trimmed : "";
}

function useUserSearch() {
  const [results, setResults] = useState<SearchResults | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [query, debouncedSearch] = useDebouncedSearch((value) => {
    const trimmed = searchableQuery(value);
    if (!trimmed) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    userSearch({ query: trimmed }, { signal: controller.signal })
      .then(({ users }) => {
        if (!controller.signal.aborted) {
          setResults({ query: trimmed, users: users ?? [], error: null });
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setResults({ query: trimmed, users: [], error: getApiErrorMessage(err) });
        }
      });
  }, SEARCH_DEBOUNCE_MS);

  const search = (value: string) => {
    abortControllerRef.current?.abort();
    debouncedSearch(value);
  };

  return { query, search, results };
}

const PersonOption = observer(function PersonOption({
  user,
  disabled,
  onClick,
}: {
  user: UserShort | CurrentUser;
  disabled: boolean;
  onClick: () => void;
}) {
  const label = useDirectMessageLabel(user);

  return (
    <button
      type="button"
      disabled={disabled}
      className={twMerge(
        "flex items-center gap-1.5 w-full min-h-[34px] px-1 rounded-6 text-left",
        "hover:bg-sidebar-button-hover-inverted-bg",
      )}
      onClick={onClick}
    >
      <DirectMessageUserpic user={user} />
      <span className="truncate text-document-text">{label}</span>
    </button>
  );
});

/**
 * Without a team, lists the direct message contacts and then the members of every team, loading
 * one team per page as the list scrolls: the memberships endpoint returns a whole team at once.
 * A team that fails to load is skipped until the picker opens again.
 */
const PeopleList = observer(function PeopleList({
  team,
  root,
  close,
}: {
  team?: TeamModel;
  root: HTMLElement | null;
  close: () => void;
}) {
  const t = useTranslations();
  const { auth, teams } = useStore();
  const directChats = useDirectChats();
  const { openDirectChat, isOpening } = useOpenDirectChat();
  const { query, search, results } = useUserSearch();
  const trimmedQuery = searchableQuery(query);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [failedTeamUids] = useState(() => observable.set<string>());

  const rosterTeams = team ? [team] : teams.sortedUserTeams.filter(({ personal }) => !personal);
  const isLoaded = (rosterTeam: TeamModel) => rosterTeam.membersUX.membersStatus === "loaded";
  // Read at call time too: a load that just failed must not be retried by a stale render
  const findNextTeam = () =>
    rosterTeams.find((rosterTeam) => !isLoaded(rosterTeam) && !failedTeamUids.has(rosterTeam.uid));
  const nextTeam = findNextTeam();
  const loadedTeams = (
    nextTeam ? rosterTeams.slice(0, rosterTeams.indexOf(nextTeam)) : rosterTeams
  ).filter(isLoaded);
  const contacts = team ? [] : directChats.chats.flatMap((chat) => chat.otherParticipant ?? []);
  const defaultPeople = uniqBy(
    [
      ...(auth.currentUser ? [auth.currentUser] : []),
      ...contacts,
      ...loadedTeams.flatMap(({ membersUX }) => membersUX.members.map((member) => member.user)),
    ],
    (user) => user.uid,
  );
  const isSearchSettled = results?.query === trimmedQuery;
  const searchResults = isSearchSettled ? results.users : [];
  const searchError = isSearchSettled ? results.error : null;
  const people = trimmedQuery ? searchResults : defaultPeople;
  const canLoadNextTeam = !trimmedQuery && !!nextTeam;
  const isLoadingNextTeam = canLoadNextTeam && nextTeam.membersUX.membersStatus === "loading";
  const isSearching = !!trimmedQuery && !isSearchSettled;

  const setSentinel = useLoadMore({
    root,
    hasMore: visibleCount < people.length || canLoadNextTeam,
    isLoading: isLoadingNextTeam,
    onLoadMore: async () => {
      if (visibleCount < people.length) {
        setVisibleCount((count) => count + PAGE_SIZE);
        return true;
      }
      const teamToLoad = trimmedQuery ? undefined : findNextTeam();
      if (!teamToLoad) return false;

      try {
        await teamToLoad.membersUX.loadMembers();
      } catch (err) {
        console.error("Failed to load team members:", err);
        runInAction(() => failedTeamUids.add(teamToLoad.uid));
      }
      return true;
    },
  });

  return (
    <>
      <input
        type="text"
        value={query}
        onChange={(e) => search(e.target.value)}
        placeholder={t("documentsSidebar.searchPeople")}
        aria-label={t("documentsSidebar.searchPeople")}
        autoCapitalize="none"
        className={twMerge(
          "w-full h-8 mb-1 px-2 rounded-6 outline-none",
          "bg-sidebar-button-hover-inverted-bg text-document-text placeholder:text-gray-500",
        )}
      />
      {people.length > 0 ? (
        <ul>
          {people.slice(0, visibleCount).map((user) => (
            <li key={user.uid}>
              <PersonOption
                user={user}
                disabled={isOpening}
                onClick={() => {
                  void openDirectChat(user.uid).then((opened) => {
                    if (opened) close();
                  });
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        trimmedQuery &&
        isSearchSettled &&
        !searchError && (
          <div className="px-1 py-2 text-gray-500">{t("documentsSidebar.noPeopleFound")}</div>
        )
      )}
      <div ref={setSentinel} aria-hidden />
      {(isLoadingNextTeam || isSearching) && (
        <div className="flex py-2">
          <Spinner />
        </div>
      )}
      <FormFieldError message={searchError} className="px-1" />
    </>
  );
});

/** Starts a direct message with a team member, a contact, or anyone found by searching. */
export const NewDirectMessagePicker = observer(function NewDirectMessagePicker({
  team,
}: {
  team?: TeamModel;
}) {
  const t = useTranslations();
  const { handlerProps, tooltip } = useTooltip({ title: t("documentsSidebar.newDirectMessage") });
  const controlTabIndex = useTreeItemControlTabIndex();
  const [panel, setPanel] = useState<HTMLElement | null>(null);

  return (
    <Popover className="flex my-auto mr-[3px] sm:mr-0">
      <span className="flex" {...handlerProps}>
        <PopoverButton
          type="button"
          tabIndex={controlTabIndex}
          aria-label={t("documentsSidebar.newDirectMessage")}
          className={SECTION_HEADER_ACTION_CLASS_NAME}
        >
          <Icon
            name="chat/NewChat"
            className={twMerge("w-5 h-5", SECTION_HEADER_ACTION_ICON_CLASS_NAME)}
          />
        </PopoverButton>
      </span>
      {tooltip}
      <PopoverPanel
        ref={setPanel}
        focus
        portal
        anchor={{ to: "bottom start", gap: 4, padding: 10 }}
        className={twMerge(
          "w-[268px] max-h-[min(60vh,420px)] overflow-y-auto no-scrollbar",
          "bg-block-bg shadow-md dark:shadow-block-inverted rounded-12 p-2 z-100 outline-none",
        )}
      >
        {({ close }) => <PeopleList team={team} root={panel} close={close} />}
      </PopoverPanel>
    </Popover>
  );
});
